import Chart, { ChartProps } from "../../Components/Chart";
import { useAsync } from "react-use";
import axios from "axios";
import { Interval } from "../../Components/getInterval";
import compoundIdsQuery from "./compoundIdsQuery";

interface CompoundServiceChartProps extends Partial<ChartProps> {
	interval: Interval;
	compoundIds: string[];
}

interface StatisticData {
	serviceTypeId: string;
	date: string;
	count: number;
	totalAmount: number;
}

type ServiceTypeIds = "Picture" | "PicturePreparation" | "LotVehicleHandling" | "LotVehicleExit";

const serviceTypes = {
	Picture: "Fotobox",
	PicturePreparation: "Vorbereitung Fotobox",
	LotVehicleHandling: "Handling Gebrauchtwagenplatz",
	LotVehicleExit: "Ausgang Gebrauchtwagenplatz"
};

export default ({ interval, compoundIds, ...props }: CompoundServiceChartProps) => {
	// react-use's useAsync compares deps by reference; compoundIds is a new array on every
	// parent render, so depend on a stable joined string instead to avoid an infinite fetch loop.
	const compoundIdsKey = compoundIds.join(",");

	const chartData = useAsync(async () => {
		if (compoundIds.length === 0) {
			return [];
		}

		const { data: services } = await axios.post<StatisticData[]>(
			`/api/compounds/kpis/vehicle-services?${compoundIdsQuery(compoundIds)}`,
			interval
		);

		const createSeries = (serviceTypeId: ServiceTypeIds, services: StatisticData[]) => ({
			name: serviceTypeId,
			title: serviceTypes[serviceTypeId],
			data: services
				.filter((vs) => vs.serviceTypeId === serviceTypeId)
				.map((vs) => ({
					x: vs.date,
					y: vs.count
				}))
		});

		return [
			createSeries("LotVehicleHandling", services),
			createSeries("PicturePreparation", services),
			createSeries("Picture", services),
			createSeries("LotVehicleExit", services)
		];
	}, [interval, compoundIdsKey]);

	return <Chart {...props} loading={chartData.loading} series={chartData.value || []} />;
};
